// Shared canvas art for SmashUp.io — richer, "alive" vehicles and destructible
// objects. Pure drawing helpers (no game state); the caller sets up the
// translate/rotate transform and passes plain options.

export function shade(hex, pct) {
  if (!hex || hex[0] !== "#" || hex.length !== 7) return hex || "#888";
  let r = parseInt(hex.slice(1, 3), 16);
  let g = parseInt(hex.slice(3, 5), 16);
  let b = parseInt(hex.slice(5, 7), 16);
  const t = pct < 0 ? 0 : 255;
  const p = Math.abs(pct) / 100;
  r = Math.round(r + (t - r) * p);
  g = Math.round(g + (t - g) * p);
  b = Math.round(b + (t - b) * p);
  return `rgb(${r},${g},${b})`;
}

export function roundRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

// ---------------- VEHICLES ----------------
// Draws a vehicle centered at (0,0) facing +x. Caller handles translate/rotate,
// invuln alpha, trail, health bar and name.
export function paintVehicle(ctx, { tier = 0, color = "#FF9500", wheelColor = "#1a1a1a", radius: r = 20, flash = false, theme = "classic", headlights = true }) {
  const wid = tier >= 4 ? 0.78 : 0.72;
  const br = r * (tier >= 4 ? 0.14 : 0.24);
  _wheels(ctx, r, tier, wheelColor);
  _body(ctx, r, tier, color, flash, wid, br);
  if (!flash) _pattern(ctx, r, wid, br, theme, color);
  _cabin(ctx, r, tier);
  _features(ctx, r, tier, color);
  if (headlights && tier < 8) {
    ctx.save();
    ctx.fillStyle = "#FFF6C0"; ctx.shadowColor = "#FFE68A"; ctx.shadowBlur = r * 0.5;
    ctx.beginPath(); ctx.arc(r * 0.86, -r * 0.5, r * 0.1, 0, 6.28); ctx.fill();
    ctx.beginPath(); ctx.arc(r * 0.86, r * 0.5, r * 0.1, 0, 6.28); ctx.fill();
    ctx.restore();
  }
}

function _wheels(ctx, r, tier, wheelColor) {
  if (tier >= 8) { // tank treads
    ctx.fillStyle = "#1c1c22";
    roundRect(ctx, -r * 1.0, -r * 0.98, r * 2.0, r * 0.34, 5); ctx.fill();
    roundRect(ctx, -r * 1.0, r * 0.64, r * 2.0, r * 0.34, 5); ctx.fill();
    ctx.fillStyle = "#0d0d10";
    for (let i = -5; i <= 5; i++) {
      ctx.fillRect(i * r * 0.18, -r * 0.98, r * 0.05, r * 0.34);
      ctx.fillRect(i * r * 0.18, r * 0.64, r * 0.05, r * 0.34);
    }
    return;
  }
  const big = tier >= 7;
  const wl = r * (big ? 0.62 : 0.5), ww = r * (big ? 0.52 : 0.42);
  const xs = tier >= 2 ? [-0.64, 0, 0.64] : [-0.62, 0.62];
  for (const xf of xs) for (const yf of [-0.8, 0.8]) {
    ctx.fillStyle = "#0e0e12";
    roundRect(ctx, xf * r - wl / 2, yf * r - ww / 2, wl, ww, ww * 0.4); ctx.fill();
    ctx.fillStyle = wheelColor;
    roundRect(ctx, xf * r - wl / 2 + wl * 0.14, yf * r - ww / 2 + ww * 0.18, wl * 0.72, ww * 0.64, ww * 0.3); ctx.fill();
    ctx.fillStyle = "rgba(255,255,255,0.35)";
    ctx.beginPath(); ctx.arc(xf * r, yf * r, ww * 0.16, 0, 6.28); ctx.fill();
  }
}

function _body(ctx, r, tier, color, flash, wid, br) {
  const grad = ctx.createLinearGradient(0, -r * 0.75, 0, r * 0.75);
  grad.addColorStop(0, shade(color, 30));
  grad.addColorStop(0.5, color);
  grad.addColorStop(1, shade(color, -32));
  ctx.fillStyle = flash ? "#ffffff" : grad;
  ctx.strokeStyle = "rgba(0,0,0,0.55)"; ctx.lineWidth = Math.max(2, r * 0.12);
  roundRect(ctx, -r * 0.9, -r * wid, r * 1.8, r * wid * 2, br);
  ctx.fill(); ctx.stroke();
  // top sheen
  ctx.fillStyle = "rgba(255,255,255,0.16)";
  roundRect(ctx, -r * 0.82, -r * (wid - 0.06), r * 1.64, r * 0.38, r * 0.16); ctx.fill();
}

// Skin PATTERN overlay — clipped to the body so each shop skin looks unique,
// not just a different colour (flames, stripes, chrome, circuitry, camo...).
function _pattern(ctx, r, wid, br, theme, color) {
  ctx.save();
  roundRect(ctx, -r * 0.9, -r * wid, r * 1.8, r * wid * 2, br);
  ctx.clip();
  const T = theme;
  if (T === "fire" || T === "magma") {
    ctx.fillStyle = "rgba(255,209,102,0.92)";
    for (const yf of [-0.42, 0, 0.42]) { ctx.beginPath(); ctx.moveTo(r * 0.9, yf * r - r * 0.16); ctx.lineTo(-r * 0.25, yf * r); ctx.lineTo(r * 0.9, yf * r + r * 0.16); ctx.closePath(); ctx.fill(); }
    ctx.fillStyle = "rgba(255,59,48,0.85)";
    for (const yf of [-0.42, 0, 0.42]) { ctx.beginPath(); ctx.moveTo(r * 0.9, yf * r - r * 0.08); ctx.lineTo(r * 0.15, yf * r); ctx.lineTo(r * 0.9, yf * r + r * 0.08); ctx.closePath(); ctx.fill(); }
  } else if (T === "neon" || T === "toxic") {
    ctx.shadowColor = shade(color, 40); ctx.shadowBlur = r * 0.5;
    ctx.fillStyle = shade(color, 62);
    ctx.fillRect(-r * 0.9, -r * 0.26, r * 1.8, r * 0.11);
    ctx.fillRect(-r * 0.9, r * 0.15, r * 1.8, r * 0.11);
    ctx.shadowBlur = 0;
    ctx.strokeStyle = shade(color, 70); ctx.lineWidth = r * 0.05; ctx.strokeRect(-r * 0.86, -r * (wid - 0.02), r * 1.72, r * (wid - 0.02) * 2);
  } else if (T === "chrome" || T === "champion") {
    ctx.fillStyle = "rgba(255,255,255,0.5)";
    for (const o of [-0.5, 0.15]) { ctx.beginPath(); ctx.moveTo(o * r - r * 0.16, -r); ctx.lineTo(o * r + r * 0.16, -r); ctx.lineTo(o * r + r * 0.44, r); ctx.lineTo(o * r + r * 0.12, r); ctx.closePath(); ctx.fill(); }
    ctx.fillStyle = "rgba(0,0,0,0.18)";
    ctx.beginPath(); ctx.moveTo(-r * 0.05 - r * 0.1, -r); ctx.lineTo(-r * 0.05 + r * 0.1, -r); ctx.lineTo(-r * 0.05 + r * 0.34, r); ctx.lineTo(-r * 0.05 + r * 0.14, r); ctx.closePath(); ctx.fill();
  } else if (T === "cyber" || T === "galaxy") {
    ctx.fillStyle = "rgba(255,255,255,0.85)";
    const dots = [[-0.6, -0.3], [-0.2, 0.35], [0.1, -0.15], [0.45, 0.28], [0.7, -0.35], [-0.4, 0.15], [0.3, -0.45], [-0.05, 0.5]];
    for (const [dx, dy] of dots) { ctx.beginPath(); ctx.arc(dx * r, dy * r, r * 0.05, 0, 6.28); ctx.fill(); }
    ctx.strokeStyle = shade(color, 55); ctx.lineWidth = r * 0.05;
    ctx.beginPath(); ctx.moveTo(-r * 0.8, -r * 0.1); ctx.lineTo(-r * 0.2, -r * 0.1); ctx.lineTo(0, r * 0.2); ctx.lineTo(r * 0.7, r * 0.2); ctx.stroke();
  } else if (T === "desert") {
    ctx.fillStyle = shade(color, -34);
    for (const [dx, dy, rad] of [[-0.5, -0.25, 0.3], [0.25, 0.2, 0.34], [0.6, -0.3, 0.24]]) { ctx.beginPath(); ctx.ellipse(dx * r, dy * r, rad * r, rad * r * 0.7, 0.5, 0, 6.28); ctx.fill(); }
    ctx.fillStyle = shade(color, 24);
    for (const [dx, dy, rad] of [[-0.1, -0.4, 0.22], [0.5, 0.35, 0.2], [-0.55, 0.3, 0.26]]) { ctx.beginPath(); ctx.ellipse(dx * r, dy * r, rad * r, rad * r * 0.7, -0.4, 0, 6.28); ctx.fill(); }
  } else { // classic — twin racing stripes
    ctx.fillStyle = "rgba(255,255,255,0.6)";
    ctx.fillRect(-r * 0.9, -r * 0.2, r * 1.8, r * 0.14);
    ctx.fillStyle = shade(color, -45);
    ctx.fillRect(-r * 0.9, r * 0.06, r * 1.8, r * 0.08);
  }
  ctx.restore();
}

function _cabin(ctx, r, tier) {
  if (tier >= 8) return; // tank has a turret instead
  ctx.fillStyle = "rgba(15,25,40,0.9)";
  roundRect(ctx, -r * 0.12, -r * 0.42, r * 0.6, r * 0.84, r * 0.14); ctx.fill();
  ctx.fillStyle = "rgba(120,205,255,0.4)";
  roundRect(ctx, r * 0.02, -r * 0.32, r * 0.32, r * 0.64, r * 0.1); ctx.fill();
}

function _features(ctx, r, tier, color) {
  const steel = "#c7ccd6", metal = "#8891a0";
  if (tier === 0) { // buggy roll cage
    ctx.strokeStyle = "rgba(15,15,15,0.75)"; ctx.lineWidth = r * 0.09;
    ctx.strokeRect(-r * 0.12, -r * 0.42, r * 0.6, r * 0.84);
  } else if (tier === 1) { // pickup bed
    ctx.fillStyle = shade(color, -48);
    roundRect(ctx, -r * 0.86, -r * 0.56, r * 0.6, r * 1.12, r * 0.1); ctx.fill();
    ctx.strokeStyle = "rgba(0,0,0,0.3)"; ctx.lineWidth = r * 0.05; ctx.stroke();
  } else if (tier === 2) { // grille bars
    ctx.strokeStyle = "rgba(0,0,0,0.45)"; ctx.lineWidth = r * 0.06;
    for (let i = 0; i < 3; i++) { const y = -r * 0.28 + i * r * 0.28; ctx.beginPath(); ctx.moveTo(r * 0.55, y); ctx.lineTo(r * 0.85, y); ctx.stroke(); }
  } else if (tier === 3) { // bulldozer blade
    ctx.fillStyle = steel; ctx.strokeStyle = "rgba(0,0,0,0.5)"; ctx.lineWidth = r * 0.08;
    roundRect(ctx, r * 0.86, -r * 0.92, r * 0.22, r * 1.84, r * 0.06); ctx.fill(); ctx.stroke();
    ctx.fillStyle = "rgba(255,255,255,0.25)"; ctx.fillRect(r * 0.9, -r * 0.9, r * 0.06, r * 1.8);
  } else if (tier === 4) { // armored turret + plate
    ctx.fillStyle = shade(color, -42);
    roundRect(ctx, -r * 0.55, -r * 0.66, r * 0.55, r * 1.32, r * 0.1); ctx.fill();
    ctx.fillStyle = metal; ctx.beginPath(); ctx.arc(0, 0, r * 0.3, 0, 6.28); ctx.fill();
    ctx.strokeStyle = "rgba(0,0,0,0.4)"; ctx.lineWidth = r * 0.06; ctx.stroke();
  } else if (tier === 5) { // side spikes
    ctx.fillStyle = steel;
    for (const yf of [-0.74, 0.74]) for (let i = -1; i <= 1; i++) {
      const x = i * r * 0.5, s = Math.sign(yf);
      ctx.beginPath(); ctx.moveTo(x - r * 0.12, yf * r); ctx.lineTo(x + r * 0.12, yf * r); ctx.lineTo(x, yf * r + s * r * 0.3); ctx.closePath(); ctx.fill();
    }
  } else if (tier === 6) { // ultimate ram + glow core
    ctx.fillStyle = steel; roundRect(ctx, r * 0.86, -r * 0.82, r * 0.16, r * 1.64, r * 0.05); ctx.fill();
    for (const yf of [-0.55, 0, 0.55]) { ctx.beginPath(); ctx.moveTo(r * 1.02, yf * r - r * 0.13); ctx.lineTo(r * 1.3, yf * r); ctx.lineTo(r * 1.02, yf * r + r * 0.13); ctx.closePath(); ctx.fill(); }
    ctx.save(); ctx.fillStyle = "#FF3B30"; ctx.shadowColor = "#FF3B30"; ctx.shadowBlur = r * 0.6;
    ctx.beginPath(); ctx.arc(-r * 0.22, 0, r * 0.16, 0, 6.28); ctx.fill(); ctx.restore();
  } else if (tier === 7) { // monster rig stacks + roll bar
    ctx.fillStyle = "#3a3a42"; ctx.fillRect(-r * 0.36, -r * 0.82, r * 0.14, r * 0.3); ctx.fillRect(-r * 0.36, r * 0.52, r * 0.14, r * 0.3);
    ctx.fillStyle = steel; roundRect(ctx, r * 0.86, -r * 0.86, r * 0.16, r * 1.72, r * 0.05); ctx.fill();
    ctx.strokeStyle = "rgba(15,15,15,0.7)"; ctx.lineWidth = r * 0.08; ctx.strokeRect(-r * 0.15, -r * 0.45, r * 0.5, r * 0.9);
  } else if (tier === 8) { // tank turret + cannon
    ctx.fillStyle = "#5b6270"; ctx.beginPath(); ctx.arc(-r * 0.08, 0, r * 0.42, 0, 6.28); ctx.fill();
    ctx.strokeStyle = "rgba(0,0,0,0.4)"; ctx.lineWidth = r * 0.06; ctx.stroke();
    ctx.fillStyle = "#3a3f49"; roundRect(ctx, r * 0.1, -r * 0.13, r * 1.15, r * 0.26, r * 0.05); ctx.fill();
    ctx.fillStyle = "#22262e"; ctx.fillRect(r * 1.2, -r * 0.17, r * 0.16, r * 0.34);
  }
}

// ---------------- OBSTACLES ----------------
// Draws an obstacle centered at (0,0). Caller applies translate/rotate/shake.
export function paintObstacle(ctx, o) {
  const r = o.radius, d = o.def, key = o.key, c = d.color;
  const dmg = o.maxHp ? 1 - o.hp / o.maxHp : 0;
  ctx.lineJoin = "round";
  switch (key) {
    case "box": _crate(ctx, r, c); break;
    case "barrel": _barrel(ctx, r, c); break;
    case "sign": _sign(ctx, r, c); break;
    case "cone": _cone(ctx, r, c); break;
    case "trash": _trash(ctx, r, c); break;
    case "hydrant": _hydrant(ctx, r, c); break;
    case "car": _car(ctx, r, c); break;
    case "van": _van(ctx, r, c); break;
    case "barrier": _barrier(ctx, r, c); break;
    case "container": _container(ctx, r, c); break;
    case "truck": _truck(ctx, r, c); break;
    case "crane": _crane(ctx, r, c); break;
    case "building": _building(ctx, r, c); break;
    default: _crate(ctx, r, c);
  }
  if (dmg > 0.35) {
    ctx.strokeStyle = "rgba(0,0,0,0.55)"; ctx.lineWidth = Math.max(1.5, r * 0.06);
    ctx.beginPath();
    ctx.moveTo(-r * 0.5, -r * 0.35); ctx.lineTo(-r * 0.1, r * 0.05); ctx.lineTo(r * 0.15, -r * 0.15); ctx.lineTo(r * 0.45, r * 0.4);
    ctx.stroke();
  }
}

function _blk(ctx, x, y, w, h, r, fill) { ctx.fillStyle = fill; roundRect(ctx, x, y, w, h, r); ctx.fill(); ctx.strokeStyle = "rgba(0,0,0,0.4)"; ctx.lineWidth = Math.max(1.5, w * 0.05); ctx.stroke(); }

function _crate(ctx, r, c) {
  _blk(ctx, -r, -r, r * 2, r * 2, r * 0.18, c);
  ctx.strokeStyle = shade(c, -35); ctx.lineWidth = r * 0.12;
  ctx.beginPath(); ctx.moveTo(-r * 0.85, -r * 0.85); ctx.lineTo(r * 0.85, r * 0.85);
  ctx.moveTo(r * 0.85, -r * 0.85); ctx.lineTo(-r * 0.85, r * 0.85); ctx.stroke();
  ctx.strokeStyle = shade(c, 25); ctx.lineWidth = r * 0.1;
  ctx.strokeRect(-r * 0.7, -r * 0.7, r * 1.4, r * 1.4);
}

function _barrel(ctx, r, c) {
  ctx.fillStyle = shade(c, -20); ctx.beginPath(); ctx.arc(0, 0, r, 0, 6.28); ctx.fill();
  ctx.fillStyle = c; ctx.beginPath(); ctx.arc(0, 0, r * 0.86, 0, 6.28); ctx.fill();
  // hazard band
  ctx.strokeStyle = "#FFCC00"; ctx.lineWidth = r * 0.22;
  ctx.beginPath(); ctx.arc(0, 0, r * 0.55, 0, 6.28); ctx.stroke();
  ctx.strokeStyle = "rgba(0,0,0,0.4)"; ctx.lineWidth = r * 0.06;
  ctx.beginPath(); ctx.arc(0, 0, r * 0.86, 0, 6.28); ctx.stroke();
  ctx.beginPath(); ctx.arc(0, 0, r * 0.3, 0, 6.28); ctx.stroke();
  // explosive glint
  ctx.fillStyle = "rgba(255,255,255,0.35)"; ctx.beginPath(); ctx.arc(-r * 0.3, -r * 0.3, r * 0.16, 0, 6.28); ctx.fill();
}

function _sign(ctx, r, c) {
  ctx.fillStyle = "#6b7280"; ctx.fillRect(-r * 0.12, -r * 0.2, r * 0.24, r * 1.1); // post
  _blk(ctx, -r, -r, r * 2, r * 1.05, r * 0.16, c);
  ctx.fillStyle = "#fff"; ctx.font = `900 ${r * 1.1}px Outfit, sans-serif`; ctx.textAlign = "center"; ctx.textBaseline = "middle";
  ctx.fillText("!", 0, -r * 0.42);
}

function _cone(ctx, r, c) {
  ctx.fillStyle = shade(c, -25); ctx.beginPath(); ctx.arc(0, 0, r, 0, 6.28); ctx.fill(); // base
  ctx.fillStyle = c; ctx.beginPath(); ctx.arc(0, 0, r * 0.78, 0, 6.28); ctx.fill();
  ctx.fillStyle = "rgba(255,255,255,0.85)"; ctx.beginPath(); ctx.arc(0, 0, r * 0.52, 0, 6.28); ctx.fill();
  ctx.fillStyle = c; ctx.beginPath(); ctx.arc(0, 0, r * 0.3, 0, 6.28); ctx.fill();
}

function _trash(ctx, r, c) {
  _blk(ctx, -r * 0.75, -r * 0.9, r * 1.5, r * 1.8, r * 0.2, c);
  ctx.fillStyle = shade(c, -35); ctx.fillRect(-r * 0.85, -r * 0.95, r * 1.7, r * 0.28); // lid
  ctx.strokeStyle = shade(c, -45); ctx.lineWidth = r * 0.08;
  for (let i = -1; i <= 1; i++) { ctx.beginPath(); ctx.moveTo(i * r * 0.4, -r * 0.55); ctx.lineTo(i * r * 0.4, r * 0.75); ctx.stroke(); }
}

function _hydrant(ctx, r, c) {
  ctx.fillStyle = shade(c, -20); ctx.fillRect(-r * 0.85, -r * 0.2, r * 1.7, r * 0.4); // side nozzles
  _blk(ctx, -r * 0.55, -r * 0.85, r * 1.1, r * 1.7, r * 0.35, c);
  ctx.fillStyle = shade(c, 25); ctx.beginPath(); ctx.arc(0, -r * 0.7, r * 0.4, Math.PI, 0); ctx.fill(); // cap
  ctx.fillStyle = "rgba(0,0,0,0.35)"; ctx.beginPath(); ctx.arc(0, 0, r * 0.22, 0, 6.28); ctx.fill();
}

function _wheelsSide(ctx, r, xs, yOff) {
  ctx.fillStyle = "#141414";
  for (const x of xs) { roundRect(ctx, x - r * 0.18, -yOff, r * 0.36, r * 0.22, r * 0.08); ctx.fill(); roundRect(ctx, x - r * 0.18, yOff - r * 0.22, r * 0.36, r * 0.22, r * 0.08); ctx.fill(); }
}

function _car(ctx, r, c) {
  _wheelsSide(ctx, r, [-r * 0.55, r * 0.55], r * 0.62);
  _blk(ctx, -r * 0.92, -r * 0.55, r * 1.84, r * 1.1, r * 0.28, c);
  ctx.fillStyle = shade(c, 22); roundRect(ctx, -r * 0.4, -r * 0.42, r * 0.72, r * 0.84, r * 0.2); ctx.fill(); // roof
  ctx.fillStyle = "rgba(120,205,255,0.5)"; roundRect(ctx, r * 0.34, -r * 0.34, r * 0.28, r * 0.68, r * 0.1); ctx.fill(); // windshield
  ctx.fillStyle = "#FFF3B0"; ctx.beginPath(); ctx.arc(r * 0.86, -r * 0.36, r * 0.1, 0, 6.28); ctx.arc(r * 0.86, r * 0.36, r * 0.1, 0, 6.28); ctx.fill();
}

function _van(ctx, r, c) {
  _wheelsSide(ctx, r, [-r * 0.6, r * 0.6], r * 0.7);
  _blk(ctx, -r * 0.95, -r * 0.65, r * 1.9, r * 1.3, r * 0.16, c);
  ctx.fillStyle = "rgba(120,205,255,0.45)"; roundRect(ctx, r * 0.45, -r * 0.45, r * 0.34, r * 0.9, r * 0.08); ctx.fill();
  ctx.strokeStyle = shade(c, -40); ctx.lineWidth = r * 0.06; ctx.beginPath(); ctx.moveTo(r * 0.25, -r * 0.6); ctx.lineTo(r * 0.25, r * 0.6); ctx.stroke();
}

function _barrier(ctx, r, c) {
  ctx.fillStyle = c; ctx.beginPath();
  ctx.moveTo(-r, r * 0.7); ctx.lineTo(-r * 0.6, -r * 0.7); ctx.lineTo(r * 0.6, -r * 0.7); ctx.lineTo(r, r * 0.7); ctx.closePath(); ctx.fill();
  ctx.strokeStyle = "rgba(0,0,0,0.4)"; ctx.lineWidth = r * 0.06; ctx.stroke();
  // hazard stripes
  ctx.save();
  ctx.beginPath(); ctx.moveTo(-r, r * 0.7); ctx.lineTo(-r * 0.6, -r * 0.7); ctx.lineTo(r * 0.6, -r * 0.7); ctx.lineTo(r, r * 0.7); ctx.closePath(); ctx.clip();
  ctx.strokeStyle = "#111"; ctx.lineWidth = r * 0.22;
  for (let x = -r * 1.5; x < r * 1.5; x += r * 0.5) { ctx.beginPath(); ctx.moveTo(x, -r); ctx.lineTo(x + r, r); ctx.stroke(); }
  ctx.restore();
}

function _container(ctx, r, c) {
  const s = r * 1.28;
  _blk(ctx, -s * 0.7, -s * 0.5, s * 1.4, s, r * 0.08, c);
  ctx.strokeStyle = shade(c, -30); ctx.lineWidth = r * 0.07;
  for (let x = -s * 0.55; x < s * 0.6; x += r * 0.22) { ctx.beginPath(); ctx.moveTo(x, -s * 0.46); ctx.lineTo(x, s * 0.46); ctx.stroke(); }
  ctx.fillStyle = shade(c, -20); // corner castings
  [[-s * 0.7, -s * 0.5], [s * 0.7 - r * 0.2, -s * 0.5], [-s * 0.7, s * 0.5 - r * 0.2], [s * 0.7 - r * 0.2, s * 0.5 - r * 0.2]].forEach(([x, y]) => ctx.fillRect(x, y, r * 0.2, r * 0.2));
}

function _truck(ctx, r, c) {
  _wheelsSide(ctx, r, [-r * 0.6, -r * 0.1, r * 0.55], r * 0.62);
  ctx.fillStyle = shade(c, -30); roundRect(ctx, -r * 0.95, -r * 0.52, r * 1.15, r * 1.04, r * 0.08); ctx.fill(); // trailer
  ctx.strokeStyle = "rgba(0,0,0,0.35)"; ctx.lineWidth = r * 0.06; ctx.stroke();
  _blk(ctx, r * 0.28, -r * 0.5, r * 0.68, r, r * 0.14, c); // cab
  ctx.fillStyle = "rgba(120,205,255,0.5)"; roundRect(ctx, r * 0.62, -r * 0.36, r * 0.28, r * 0.72, r * 0.08); ctx.fill();
}

function _crane(ctx, r, c) {
  _blk(ctx, -r * 0.6, -r * 0.55, r * 1.2, r * 1.1, r * 0.12, c); // base cab
  ctx.strokeStyle = shade(c, -25); ctx.lineWidth = r * 0.16;
  ctx.beginPath(); ctx.moveTo(0, 0); ctx.lineTo(r * 1.5, -r * 0.35); ctx.stroke(); // arm
  ctx.strokeStyle = "rgba(0,0,0,0.4)"; ctx.lineWidth = r * 0.05;
  for (let i = 1; i < 6; i++) { const x = i / 6; ctx.beginPath(); ctx.moveTo(r * 1.5 * x, -r * 0.35 * x - r * 0.08); ctx.lineTo(r * 1.5 * x, -r * 0.35 * x + r * 0.08); ctx.stroke(); }
  ctx.strokeStyle = "#333"; ctx.lineWidth = r * 0.04; ctx.beginPath(); ctx.moveTo(r * 1.45, -r * 0.34); ctx.lineTo(r * 1.45, r * 0.3); ctx.stroke();
  ctx.fillStyle = "#555"; ctx.fillRect(r * 1.38, r * 0.3, r * 0.16, r * 0.16); // hook
}

function _building(ctx, r, c) {
  const s = r * 1.35;
  ctx.fillStyle = shade(c, 12); roundRect(ctx, -s * 0.7, -s * 0.72, s * 1.4, s * 1.5, r * 0.06); ctx.fill();
  ctx.strokeStyle = "rgba(0,0,0,0.45)"; ctx.lineWidth = r * 0.08; ctx.stroke();
  ctx.fillStyle = shade(c, -25); roundRect(ctx, -s * 0.75, -s * 0.82, s * 1.5, s * 0.16, r * 0.04); ctx.fill(); // roof
  // lit windows grid
  for (let ix = -1; ix <= 1; ix++) for (let iy = -1; iy <= 2; iy++) {
    const lit = (ix + iy) % 2 === 0;
    ctx.fillStyle = lit ? "rgba(255,214,90,0.85)" : "rgba(120,150,190,0.4)";
    ctx.fillRect(ix * s * 0.4 - s * 0.13, iy * s * 0.32 - s * 0.4, s * 0.26, s * 0.22);
  }
  ctx.fillStyle = "rgba(0,0,0,0.5)"; ctx.fillRect(-s * 0.14, s * 0.5, s * 0.28, s * 0.28); // door
}
